// components/historial-pagos/exportadores.ts
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ReportePago } from '../../services/reportePagosService';

// Configuración de estilos PDF
const brandColors = {
    primary: '#c1348b',
    secondary: '#61257b',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    dark: '#0F172A',
    gray: '#64748B'
};

// Función auxiliar para formatear fechas
const formatFecha = (fechaString: string) => {
    return new Date(fechaString).toLocaleDateString('es-BO', { 
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Función para generar timestamp en formato YYYYMMDD_HHmmss
const generarTimestamp = () => {
    const ahora = new Date();
    const fecha = ahora.toISOString().split('T')[0].replace(/-/g, '');
    const hora = ahora.toTimeString().split(' ')[0].replace(/:/g, '');
    return `${fecha}_${hora}`;
};

export const exportToPDF = (reporte: ReportePago) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let finalY = 0;

    // Título
    doc.setFontSize(18);
    doc.setTextColor(brandColors.dark);
    doc.text('Reporte de Pagos - Historial por Sesiones', 14, 22);

    // Subtítulo
    doc.setFontSize(12);
    doc.setTextColor(brandColors.gray);
    doc.text(`Cita: ${reporte.codigo}`, 14, 32);
    doc.text(`Cliente: ${reporte.cliente_info.nombre}`, 14, 38);

    // Resumen
    doc.setFontSize(14);
    doc.setTextColor(brandColors.dark);
    doc.text('Resumen Financiero', 14, 50);

    const resumenData = [
        ['Total de la Cita', `Bs ${Number(reporte.total).toFixed(2)}`],
        ['Total Pagado', `Bs ${Number(reporte.monto_pagado).toFixed(2)}`],
        ['Deuda Pendiente', `Bs ${Number(reporte.deuda_total).toFixed(2)}`],
        ['Porcentaje Pagado', `${((reporte.monto_pagado / reporte.total) * 100).toFixed(1)}%`]
    ];

    autoTable(doc, {
        startY: 55,
        head: [['Concepto', 'Valor']],
        body: resumenData,
        theme: 'grid',
        headStyles: { fillColor: brandColors.secondary, textColor: '#FFFFFF' },
        margin: { left: 14, right: 14 }
    });

    finalY = (doc as any).lastAutoTable.finalY + 15;

    // Detalle por Sesiones
    doc.setFontSize(14);
    doc.setTextColor(brandColors.dark);
    doc.text('Detalle por Sesiones', 14, finalY);
    finalY += 10;

    reporte.sesiones_detalle.forEach((sesion, index) => {
        if (finalY > 250) {
            doc.addPage();
            finalY = 20;
        }

        // Formatear fechas
        const fechaInicio = formatFecha(sesion.fecha_inicio);
        const fechaFin = formatFecha(sesion.fecha_fin);
        
        // Encabezado de sesión - SIEMPRE mostrar ambas fechas
        doc.setFontSize(12);
        doc.setTextColor(brandColors.primary);
        
        // SIEMPRE mostrar ambas fechas separadas por guión
        doc.text(`Sesión ${index + 1}: ${fechaInicio} - ${fechaFin}`, 14, finalY);
        finalY += 7;

        doc.setFontSize(10);
        doc.setTextColor(brandColors.gray);
        doc.text(`${sesion.horas} hora(s) | Estado: ${sesion.estado_pago}`, 14, finalY);
        finalY += 10;

        // Datos de la sesión
        const sesionData = [
            ['Precio Original', `Bs ${Number(sesion.precio_original).toFixed(2)}`],
            ['Precio con Descuento', `Bs ${Number(sesion.precio_con_descuento).toFixed(2)}`],
            ['Pagado', `Bs ${Number(sesion.monto_pagado).toFixed(2)}`],
            ['Saldo Pendiente', `Bs ${Number(sesion.saldo_pendiente).toFixed(2)}`]
        ];

        autoTable(doc, {
            startY: finalY,
            body: sesionData,
            theme: 'grid',
            margin: { left: 14, right: 14 }
        });

        finalY = (doc as any).lastAutoTable.finalY + 10;

        // Pagos de la sesión
        if (sesion.pagos.length > 0) {
            doc.setFontSize(11);
            doc.setTextColor(brandColors.dark);
            doc.text('Pagos Registrados:', 14, finalY);
            finalY += 7;

            const pagosData = sesion.pagos.map(pago => [
                formatFecha(pago.fecha_pago),
                pago.tipo_pago_nombre,
                `Bs ${Number(pago.monto).toFixed(2)}`,
                pago.codigo
            ]);

            autoTable(doc, {
                startY: finalY,
                head: [['Fecha', 'Tipo', 'Monto', 'Código']],
                body: pagosData,
                theme: 'grid',
                headStyles: { fillColor: brandColors.gray, textColor: '#FFFFFF' },
                margin: { left: 14, right: 14 }
            });

            finalY = (doc as any).lastAutoTable.finalY + 15;
        } else {
            doc.setFontSize(10);
            doc.setTextColor(brandColors.gray);
            doc.text('No hay pagos registrados para esta sesión', 14, finalY);
            finalY += 15;
        }
    });

    // Historial completo
    if (finalY > 200) {
        doc.addPage();
        finalY = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(brandColors.dark);
    doc.text('Historial Completo de Pagos', 14, finalY);
    finalY += 10;

    const historialData = reporte.historial_pagos.map(pago => [
        formatFecha(pago.fecha_pago),
        pago.origen,
        pago.tipo_pago_nombre,
        Number(pago.monto).toFixed(2),
        pago.codigo
    ]);

    autoTable(doc, {
        startY: finalY,
        head: [['Fecha', 'Origen', 'Tipo', 'Monto (Bs)', 'Código']],
        body: historialData,
        theme: 'striped',
        headStyles: { fillColor: brandColors.dark, textColor: '#FFFFFF' },
        margin: { left: 14, right: 14 }
    });

    // Pie de página
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(brandColors.gray);
        doc.text(
            `Página ${i} de ${pageCount} | Generado el ${new Date().toLocaleDateString('es-BO')} ${new Date().toLocaleTimeString('es-BO')}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    }

    const timestamp = generarTimestamp();
    const codigoLimpio = reporte.codigo.replace(/\//g, '_');
    doc.save(`historial_pagos_${codigoLimpio}_${timestamp}.pdf`);
};

export const exportToExcel = (reporte: ReportePago) => {
    const wb = XLSX.utils.book_new();

    // Hoja de resumen
    const resumenData = [
        ['Reporte de Pagos - Historial por Sesiones'],
        ['Cita:', reporte.codigo],
        ['Cliente:', reporte.cliente_info.nombre],
        ['Email:', reporte.cliente_info.email],
        [],
        ['RESUMEN FINANCIERO'],
        ['Total de la Cita', Number(reporte.total).toFixed(2)],
        ['Total Pagado', Number(reporte.monto_pagado).toFixed(2)],
        ['Deuda Pendiente', Number(reporte.deuda_total).toFixed(2)],
        ['Porcentaje Pagado', `${((reporte.monto_pagado / reporte.total) * 100).toFixed(1)}%`],
        [],
        ['DETALLE POR SESIONES']
    ];

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);

    // Hoja de sesiones
    const sesionesData = [
        ['Sesión', 'Fecha Inicio', 'Fecha Fin', 'Horas', 'Precio Original', 'Precio con Descuento', 'Pagado', 'Saldo', 'Estado', 'Pagos Registrados']
    ];

    reporte.sesiones_detalle.forEach((sesion, index) => {
        const fechaInicio = formatFecha(sesion.fecha_inicio);
        const fechaFin = formatFecha(sesion.fecha_fin);
        
        sesionesData.push([
            `Sesión ${index + 1}`,
            fechaInicio,
            fechaFin,
            String(sesion.horas),
            Number(sesion.precio_original).toFixed(2),
            Number(sesion.precio_con_descuento).toFixed(2),
            Number(sesion.monto_pagado).toFixed(2),
            Number(sesion.saldo_pendiente).toFixed(2),
            sesion.estado_pago,
            String(sesion.pagos.length)
        ]);

        // Agregar pagos de esta sesión
        if (sesion.pagos.length > 0) {
            sesionesData.push(['Pagos de esta sesión:', '', '', '', '', '', '', '', '', '']);
            sesionesData.push(['Fecha', 'Hora', 'Tipo de Pago', 'Monto', 'Código']);
            sesion.pagos.forEach(pago => {
                const fecha = new Date(pago.fecha_pago);
                sesionesData.push([
                    fecha.toLocaleDateString('es-BO', { timeZone: 'UTC' }),
                    fecha.toLocaleTimeString('es-BO', { timeZone: 'UTC' }),
                    pago.tipo_pago_nombre,
                    Number(pago.monto).toFixed(2),
                    pago.codigo
                ]);
            });
            sesionesData.push([]);
        }
    });

    const wsSesiones = XLSX.utils.aoa_to_sheet(sesionesData);

    // Hoja de historial completo
    const historialData = [
        ['HISTORIAL COMPLETO DE PAGOS'],
        ['Fecha', 'Hora', 'Origen', 'Tipo de Pago', 'Monto (Bs)', 'Código']
    ];

    reporte.historial_pagos.forEach(pago => {
        const fecha = new Date(pago.fecha_pago);
        historialData.push([
            fecha.toLocaleDateString('es-BO', { timeZone: 'UTC' }),
            fecha.toLocaleTimeString('es-BO', { timeZone: 'UTC' }),
            pago.origen,
            pago.tipo_pago_nombre,
            Number(pago.monto).toFixed(2),
            pago.codigo
        ]);
    });

    const wsHistorial = XLSX.utils.aoa_to_sheet(historialData);

    // Agregar hojas al libro
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');
    XLSX.utils.book_append_sheet(wb, wsSesiones, 'Sesiones');
    XLSX.utils.book_append_sheet(wb, wsHistorial, 'Historial Completo');

    // Ajustar anchos de columnas
    const colWidths = [
        { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }
    ];
    wsSesiones['!cols'] = colWidths;

    // Guardar archivo
    const timestamp = generarTimestamp();
    const codigoLimpio = reporte.codigo.replace(/\//g, '_');
    XLSX.writeFile(wb, `historial_pagos_${codigoLimpio}_${timestamp}.xlsx`);
};